import React from 'react'
import {
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Popconfirm,
  Tooltip,
  Typography
} from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { sellerEmployeesApi } from 'api/sellerEmployees'
import {
  ALL_SELLER_PERMISSIONS,
  ESellerEmployeeStatus,
  ESellerPermission,
  IConfirmOwnershipTransferRequest,
  ISellerEmployee,
  SELLER_PERMISSION_LABELS
} from 'models/sellerEmployees'
import { appActions } from 'store/app'
import { useAppDispatch } from 'shared/hooks/useReduxHooks'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import { OwnershipTransferModal } from 'components/employees/OwnershipTransferModal/OwnershipTransferModal'
import styles from './SellerEmployees.module.scss'

interface IEmployeeForm {
  name: string
  surname: string
  email: string
  permissions: ESellerPermission[]
}

interface ISellerEmployeeModalProps {
  open: boolean
  // null — приглашение нового сотрудника
  employee: ISellerEmployee | null
  sellerId?: string
  // передать магазин может только сам владелец, не супер-админ из карточки продавца
  canTransferOwnership: boolean
  onClose: () => void
  onSaved: (employee: ISellerEmployee) => void
  onFired: (employee: ISellerEmployee) => void
  onRevoked: (id: string) => void
}

const PERMISSION_OPTIONS = ALL_SELLER_PERMISSIONS.map((permission) => ({
  value: permission,
  label: SELLER_PERMISSION_LABELS[permission]
}))

const HelpIcon: React.FC<{ title: string }> = ({ title }) => (
  <Tooltip title={title}>
    <QuestionCircleOutlined className={styles.helpIcon} />
  </Tooltip>
)

export const SellerEmployeeModal: React.FC<ISellerEmployeeModalProps> = ({
  open,
  employee,
  sellerId,
  canTransferOwnership,
  onClose,
  onSaved,
  onFired,
  onRevoked
}) => {
  const dispatch = useAppDispatch()
  const [form] = Form.useForm<IEmployeeForm>()
  const [isSaving, setIsSaving] = React.useState(false)
  const [isActing, setIsActing] = React.useState(false)
  const [isTransferOpen, setIsTransferOpen] = React.useState(false)
  const permissions: ESellerPermission[] =
    Form.useWatch('permissions', form) ?? []
  const isEdit = Boolean(employee)
  // имя — в аккаунте, он появляется, когда приглашение примут
  const hasAccount = Boolean(employee?.user_id)
  const fullName = employee ? `${employee.name} ${employee.surname}`.trim() : ''

  React.useEffect(() => {
    if (!open) return
    form.resetFields()
    if (employee) {
      const { name, surname, email, permissions: saved } = employee
      form.setFieldsValue({ name, surname, email, permissions: saved })
    } else {
      form.setFieldsValue({ permissions: ALL_SELLER_PERMISSIONS })
    }
  }, [open, employee, form])

  const onSubmit = async () => {
    let values: IEmployeeForm
    try {
      values = await form.validateFields()
    } catch {
      // ошибки уже показаны под полями
      return
    }

    setIsSaving(true)
    try {
      if (employee) {
        const { data } = await sellerEmployeesApi.updateEmployee(
          employee._id,
          {
            ...(hasAccount && {
              name: values.name,
              surname: values.surname?.trim() || undefined
            }),
            permissions: values.permissions
          },
          sellerId
        )
        showMessage('success', 'Изменения сохранены', '')
        onSaved(data)
      } else {
        const { data } = await sellerEmployeesApi.inviteEmployee({
          email: values.email,
          permissions: values.permissions,
          seller_id: sellerId
        })
        showMessage(
          'success',
          'Сотрудник приглашён',
          `Письмо со ссылкой-приглашением отправлено на ${data.email}`
        )
        onSaved(data)
      }
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSaving(false)
    }
  }

  const runAction = async (action: () => Promise<void>) => {
    try {
      setIsActing(true)
      await action()
    } catch (e) {
      showServerError(e)
    } finally {
      setIsActing(false)
    }
  }

  const fireEmployee = (id: string) =>
    runAction(async () => {
      const { data } = await sellerEmployeesApi.fireEmployee(id, sellerId)
      showMessage('success', 'Сотрудник уволен', 'Доступ к кабинету закрыт')
      onFired(data)
    })

  const resendInvitation = (id: string) =>
    runAction(async () => {
      const { data } = await sellerEmployeesApi.resendInvitation(id, sellerId)
      showMessage('success', 'Приглашение отправлено повторно', '')
      onSaved(data)
    })

  const revokeInvitation = (id: string) =>
    runAction(async () => {
      await sellerEmployeesApi.revokeInvitation(id, sellerId)
      showMessage(
        'success',
        'Приглашение отозвано',
        'Ссылка из письма больше не действует'
      )
      onRevoked(id)
    })

  // Прежний владелец остаётся в кабинете сотрудником: новая сессия уже с его
  // правами, раздел «Сотрудники» закроется и кабинет откроет главную
  const confirmTransfer = async (codes: IConfirmOwnershipTransferRequest) => {
    if (!employee) return
    const { data } = await sellerEmployeesApi.confirmOwnershipTransfer(
      employee._id,
      codes
    )
    showMessage(
      'success',
      'Магазин передан',
      `Владелец магазина теперь ${fullName}. Вы остаётесь в команде сотрудником со всеми разделами`
    )
    setIsTransferOpen(false)
    onClose()
    dispatch(appActions.setToken(data))
  }

  const isAllChecked = permissions.length === ALL_SELLER_PERMISSIONS.length

  const renderManageActions = () => {
    if (!employee) return null

    if (employee.status === ESellerEmployeeStatus.INVITED) {
      return (
        <>
          <Popconfirm
            title='Отозвать приглашение?'
            description='Ссылка из письма перестанет работать.'
            okText='Отозвать'
            cancelText='Отмена'
            okButtonProps={{ danger: true }}
            onConfirm={() => revokeInvitation(employee._id)}
          >
            <Button danger disabled={isActing}>
              Отозвать приглашение
            </Button>
          </Popconfirm>
          <Button
            disabled={isActing}
            onClick={() => resendInvitation(employee._id)}
          >
            Отправить повторно
          </Button>
        </>
      )
    }

    if (employee.status !== ESellerEmployeeStatus.ACTIVE) return null

    return (
      <>
        <Popconfirm
          title='Уволить сотрудника?'
          description='Доступ к кабинету закроется сразу.'
          okText='Уволить'
          cancelText='Отмена'
          okButtonProps={{ danger: true }}
          onConfirm={() => fireEmployee(employee._id)}
        >
          <Button danger disabled={isActing}>
            Уволить
          </Button>
        </Popconfirm>
        {canTransferOwnership && employee.user_id && (
          <Button disabled={isActing} onClick={() => setIsTransferOpen(true)}>
            Передать владение
          </Button>
        )}
      </>
    )
  }

  return (
    <>
      <Modal
        centered
        open={open}
        title={isEdit ? 'Сотрудник' : 'Пригласить сотрудника'}
        onCancel={onClose}
        forceRender
        footer={
          <div className={styles.modalFooter}>
            <div className={styles.modalFooterGroup}>
              {renderManageActions()}
            </div>
            <div className={styles.modalFooterGroup}>
              <Button onClick={onClose}>Отмена</Button>
              <Button type='primary' loading={isSaving} onClick={onSubmit}>
                {isEdit ? 'Сохранить' : 'Пригласить'}
              </Button>
            </div>
          </div>
        }
      >
        <Form form={form} layout='vertical' requiredMark={false}>
          {hasAccount && (
            <div className={styles.nameRow}>
              <Form.Item
                name='name'
                label='Имя'
                rules={[
                  { required: true, whitespace: true, message: 'Укажите имя' }
                ]}
              >
                <Input maxLength={100} />
              </Form.Item>
              <Form.Item name='surname' label='Фамилия'>
                <Input maxLength={100} />
              </Form.Item>
            </div>
          )}
          <Form.Item
            name='email'
            label={
              <span className={styles.labelWithHelp}>
                Почта
                {!isEdit && (
                  <HelpIcon title='На почту придёт письмо со ссылкой-приглашением, она действует 7 дней. По ссылке сотрудник указывает имя, задаёт пароль и подтверждает телефон по SMS, после этого входит в кабинет под этой почтой.' />
                )}
              </span>
            }
            rules={[
              { required: true, message: 'Укажите почту' },
              { type: 'email', message: 'Некорректная почта' }
            ]}
          >
            <Input disabled={isEdit} inputMode='email' autoComplete='off' />
          </Form.Item>
          <Form.Item
            label={
              <span className={styles.labelWithHelp}>
                Доступ к разделам
                <HelpIcon title='Основная информация, уведомления и поддержка открыты всегда. Банковские сведения и данные магазина сотрудник может изменить только по коду из SMS, которое придёт владельцу магазина.' />
              </span>
            }
          >
            <Checkbox
              className={styles.allPermissions}
              checked={isAllChecked}
              indeterminate={permissions.length > 0 && !isAllChecked}
              onChange={(e) =>
                form.setFieldValue(
                  'permissions',
                  e.target.checked ? ALL_SELLER_PERMISSIONS : []
                )
              }
            >
              Все разделы
            </Checkbox>
            <Form.Item name='permissions' noStyle>
              <Checkbox.Group
                className={styles.permissions}
                options={PERMISSION_OPTIONS}
              />
            </Form.Item>
          </Form.Item>
        </Form>
      </Modal>
      <OwnershipTransferModal
        open={isTransferOpen}
        title='Передать владение магазином'
        warning={
          <>
            <Typography.Text>
              Владельцем магазина станет {fullName}. Вы останетесь в команде
              сотрудником с доступом ко всем разделам. Управлять сотрудниками
              будет новый владелец, банковские сведения и данные магазина вы
              сможете изменить только по коду с его телефона. Вернуть магазин
              сможет только он.
            </Typography.Text>
            <Typography.Text>
              Для подтверждения пришлём два кода: по SMS на ваш телефон и на вашу
              почту.
            </Typography.Text>
          </>
        }
        requestCodes={async () => {
          const { data } = await sellerEmployeesApi.requestOwnershipTransfer(
            employee?._id ?? ''
          )
          return data
        }}
        confirmCodes={confirmTransfer}
        onClose={() => setIsTransferOpen(false)}
      />
    </>
  )
}
